import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Send, ChevronDown, ChevronUp, Phone, PhoneOff, Info, ExternalLink, Download, Eye } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getConfiguratorNummerOpties } from "@/data/telefonie-nummers";
import { generateOffertePDF, generateOfferteNummer, loadImageAsBase64, type OfferteData } from "@/lib/generate-offerte-pdf";
import { getDienstInfo } from "@/data/offerte-diensten";
import logoRinqel from "@/assets/logo-rinqel.png";

/* ─── Price data sourced from site ─── */

interface ConfigItem {
  id: string;
  category: string;
  name: string;
  price: number;
  unit: string;
  perUser?: boolean;
  perUnit?: boolean;
  defaultQty?: number;
  tooltip?: string;
  href?: string;
}

interface NumberOption {
  id: string;
  type: string;
  label: string;
  price: number;
  unit: string;
  eenmalig?: number;
  tooltip?: string;
  href?: string;
}

/* ── Werkplek items ── */
const werkplekItems: ConfigItem[] = [
  { id: "wp-cloud-only", category: "Werkplek", name: "Managed Cloud Only Werkplek", price: 25, unit: "per gebruiker/maand", perUser: true, tooltip: "Volledig beheerde cloud-werkplek met Microsoft 365, beveiliging en monitoring — zonder fysieke pc.", href: "/diensten/werkplek/managed-cloud-only" },
  { id: "wp-managed", category: "Werkplek", name: "Managed Werkplek", price: 50, unit: "per gebruiker/maand", perUser: true, tooltip: "Volledig beheerde werkplek inclusief beheer hardware, Microsoft 365, monitoring en support.", href: "/diensten/werkplek/managed" },
  { id: "wp-kiosk", category: "Werkplek", name: "Managed Kiosk Werkplek", price: 25, unit: "per gebruiker/maand", perUser: true, tooltip: "Beheerde gedeelde werkplek voor omgevingen waar meerdere medewerkers één apparaat gebruiken.", href: "/diensten/werkplek/kiosk" },
  { id: "wp-mobile-device", category: "Werkplek", name: "Managed Mobile Device Only", price: 17.5, unit: "per gebruiker/maand", perUser: true, tooltip: "Beheer en beveilig zakelijke smartphones en tablets met beleid, apps en encryptie.", href: "/diensten/werkplek/mobile-device" },
  { id: "wp-mailbox", category: "Werkplek", name: "Losse Mailbox (Exchange Online)", price: 7, unit: "per gebruiker/maand", perUser: true, tooltip: "Professionele zakelijke e-mail via Exchange Online met 50 GB opslag en agenda.", href: "/diensten/werkplek/exchange-online" },
  { id: "wp-shared-mailbox", category: "Werkplek", name: "Gedeelde Mailbox", price: 2, unit: "per gebruiker/maand", perUser: true, tooltip: "Gedeeld e-mailadres (bijv. info@) dat door meerdere medewerkers gebruikt kan worden." },
];

/* ── Optionele werkplek diensten ── */
const optioneleItems: ConfigItem[] = [
  { id: "opt-keeper", category: "Optioneel", name: "Keeper Wachtwoordmanager", price: 6, unit: "per gebruiker/maand", perUser: true, tooltip: "Veilige wachtwoordkluis voor je hele team. Genereer, bewaar en deel wachtwoorden zonder risico.", href: "/diensten/werkplek/keeper" },
  { id: "opt-signatures", category: "Optioneel", name: "Centrale e-mailhandtekeningen", price: 1.35, unit: "per gebruiker/maand (min. 10)", perUser: true, tooltip: "Professionele, uniforme e-mailhandtekeningen voor je hele organisatie — centraal beheerd.", href: "/diensten/werkplek/email-handtekeningen" },
];

/* ── Internet ── */
const internetItems: ConfigItem[] = [
  { id: "inet-vdsl", category: "Internet", name: "Rinqel VDSL", price: 55, unit: "per maand", tooltip: "Zakelijk VDSL internet met vaste IP, professionele router en monitoring.", href: "/diensten/internet/vdsl" },
  { id: "inet-glasvezel", category: "Internet", name: "Rinqel Glasvezel", price: 65, unit: "per maand", tooltip: "Supersnel glasvezel internet met symmetrische snelheden, vaste IP en monitoring.", href: "/diensten/internet" },
  { id: "inet-starlink", category: "Internet", name: "Rinqel met Starlink", price: 65, unit: "per maand", tooltip: "Satelliet-internet voor locaties zonder glasvezel of VDSL — overal bereikbaar." },
];

/* ── Netwerk ── */
const netwerkItems: ConfigItem[] = [
  { id: "net-switch", category: "Netwerk", name: "Managed Switch", price: 5, unit: "per switch/maand", perUnit: true, defaultQty: 1, tooltip: "Beheerde netwerkswitch met monitoring, firmware-updates en proactief onderhoud." },
  { id: "net-ap", category: "Netwerk", name: "Managed Access Point (WiFi)", price: 2.5, unit: "per AP/maand", perUnit: true, defaultQty: 1, tooltip: "Beheerd WiFi-accesspoint met monitoring, gastnetwerk en automatische updates." },
];

/* ── Implementatiekosten (eenmalig) ── */
const IMPLEMENTATIE_PRICE = 225; // eenmalig per werkplek
const IMPLEMENTATIE_IDS = new Set(["wp-cloud-only", "wp-managed", "wp-kiosk", "wp-mobile-device", "wp-mailbox"]);

/* ── Telefonie: Centrale ── */
const CENTRALE_PRICE = 15; // per maand

/* ── Telefonie: Nummers (automatisch gegenereerd vanuit gedeelde data) ── */
const nummerOpties = getConfiguratorNummerOpties();

/* ── Telefonie: Gebruiker-opties ── */
const telefonieGebruikerItems: ConfigItem[] = [
  { id: "tel-voip", category: "Telefonie", name: "VoIP Aansluiting", price: 6, unit: "per gebruiker/maand", perUser: true, tooltip: "Vaste telefonie via internet met alle professionele functies zoals doorschakelen, wachtrij en voicemail.", href: "/diensten/telefonie" },
  { id: "tel-teams", category: "Telefonie", name: "Bellen met Teams", price: 5, unit: "per gebruiker/maand", perUser: true, tooltip: "Bel rechtstreeks vanuit Microsoft Teams met je zakelijke nummer — geen extra app nodig.", href: "/diensten/telefonie" },
];

const telefonieOptieItems: ConfigItem[] = [
  { id: "tel-vamo", category: "Telefonie", name: "VAMO — Vast-Mobiel Integratie", price: 2.5, unit: "per gebruiker/maand", perUser: true, tooltip: "Bel en gebeld worden op je mobiel met je vaste zakelijke nummer — alsof je op kantoor zit.", href: "/diensten/telefonie/functies" },
  { id: "tel-crm", category: "Telefonie", name: "CRM & ERP koppeling", price: 5, unit: "per gebruiker/maand", perUser: true, tooltip: "Koppel je telefonie aan je CRM of ERP. Zie direct klantgegevens bij inkomende gesprekken.", href: "/diensten/telefonie/functies" },
];

/* ─── Helpers ─── */
const fmt = (n: number) => n.toFixed(2).replace(".", ",");

const Checkbox = ({ checked }: { checked: boolean }) => (
  <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${checked ? "border-primary bg-primary" : "border-muted-foreground/30"}`}>
    {checked && <Check className="h-3 w-3 text-primary-foreground" />}
  </div>
);

const ItemTooltip = ({ tooltip, href }: { tooltip?: string; href?: string }) => {
  if (!tooltip) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex items-center text-muted-foreground/50 hover:text-primary transition-colors cursor-help"
          onClick={(e) => e.stopPropagation()}
        >
          <Info className="h-3.5 w-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
        <p>{tooltip}</p>
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-primary hover:underline font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            Meer informatie <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

const SectionHeader = ({ title, children }: { title: string; children?: React.ReactNode }) => (
  <div className="mb-4 flex items-center justify-between">
    <h3 className="text-sm font-semibold">{title}</h3>
    {children}
  </div>
);

/* ─── Main component ─── */
const PriceConfigurator = () => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [wpQty, setWpQty] = useState<Record<string, number>>({});
  const [centraleActive, setCentraleActive] = useState(false);
  const [selectedNummers, setSelectedNummers] = useState<Set<string>>(new Set());
  const [nummerQty, setNummerQty] = useState<Record<string, number>>({});
  const [telQty, setTelQty] = useState<Record<string, number>>({});
  const [unitQty, setUnitQty] = useState<Record<string, number>>({ "net-switch": 1, "net-ap": 1 });
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ naam: "", bedrijf: "", email: "", telefoon: "" });
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null);

  // Pre-load logo as base64
  useEffect(() => {
    loadImageAsBase64(logoRinqel).then(setLogoBase64).catch(() => {});
  }, []);

  const getSelectedDiensten = () => {
    const diensten: { id: string; info: NonNullable<ReturnType<typeof getDienstInfo>>; qty: number }[] = [];

    // Werkplek items
    werkplekItems.forEach((item) => {
      if (!selected.has(item.id)) return;
      const info = getDienstInfo(item.id);
      if (info) diensten.push({ id: item.id, info, qty: wpQty[item.id] || 1 });
    });

    // Optionele items
    optioneleItems.forEach((item) => {
      if (!selected.has(item.id)) return;
      const info = getDienstInfo(item.id);
      if (info) diensten.push({ id: item.id, info, qty: item.id === "opt-signatures" ? Math.max(totalWerkplekUsers, 10) : totalWerkplekUsers });
    });

    // Internet
    internetItems.forEach((item) => {
      if (!selected.has(item.id)) return;
      const info = getDienstInfo(item.id);
      if (info) diensten.push({ id: item.id, info, qty: 1 });
    });

    // Netwerk
    netwerkItems.forEach((item) => {
      if (!selected.has(item.id)) return;
      const info = getDienstInfo(item.id);
      if (info) diensten.push({ id: item.id, info, qty: unitQty[item.id] || 1 });
    });

    // Telefonie
    if (centraleActive) {
      const centraleInfo = getDienstInfo("tel-centrale");
      if (centraleInfo) diensten.push({ id: "tel-centrale", info: centraleInfo, qty: 1 });

      telefonieGebruikerItems.forEach((item) => {
        if (!selected.has(item.id)) return;
        const info = getDienstInfo(item.id);
        if (info) diensten.push({ id: item.id, info, qty: telQty[item.id] || 1 });
      });

      telefonieOptieItems.forEach((item) => {
        if (!selected.has(item.id)) return;
        const info = getDienstInfo(item.id);
        const voipQty = selected.has("tel-voip") ? (telQty["tel-voip"] || 1) : 0;
        if (info) diensten.push({ id: item.id, info, qty: Math.min(telQty[item.id] || 1, voipQty) });
      });
    }

    return diensten;
  };

  const buildOfferteData = (): OfferteData => ({
    klant: formData,
    breakdown,
    total,
    eenmalig,
    datum: new Date().toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" }),
    offerteNummer: generateOfferteNummer(),
    logoBase64: logoBase64 || undefined,
    diensten: getSelectedDiensten(),
  });

  const handlePreviewPDF = () => {
    if (!formData.naam || !formData.email) return;
    const doc = generateOffertePDF(buildOfferteData());
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    setPdfPreviewUrl(url);
    window.open(url, "_blank");
  };

  const handleDownloadPDF = () => {
    if (!formData.naam || !formData.email) return;
    const data = buildOfferteData();
    const doc = generateOffertePDF(data);
    doc.save(`Offerte-Rinqel-${data.offerteNummer}.pdf`);
  };

  /* Total werkplek users across all selected werkplek items */
  const totalWerkplekUsers = useMemo(() => {
    return werkplekItems.reduce((sum, item) => {
      if (!selected.has(item.id)) return sum;
      return sum + (wpQty[item.id] || 1);
    }, 0);
  }, [selected, wpQty]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleNummer = (id: string) => {
    setSelectedNummers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasAtLeastOneNumber = selectedNummers.size > 0;
  const telGebruikerIds = telefonieGebruikerItems.map((i) => i.id);
  const telOptieIds = telefonieOptieItems.map((i) => i.id);
  const allTelIds = [...telGebruikerIds, ...telOptieIds];

  /* ── Waterval: Centrale uit → nummers, gebruikers & opties uit ── */
  useEffect(() => {
    if (!centraleActive) {
      setSelectedNummers(new Set());
      setSelected((prev) => {
        const next = new Set(prev);
        allTelIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, [centraleActive]);

  /* ── Waterval: Laatste nummer gedeselecteerd → centrale uit ── */
  const prevHadNumbers = useRef(false);
  useEffect(() => {
    if (centraleActive && prevHadNumbers.current && selectedNummers.size === 0) {
      setCentraleActive(false);
    }
    prevHadNumbers.current = selectedNummers.size > 0;
  }, [selectedNummers.size, centraleActive]);

  /* ── Waterval: VoIP uit → Teams & opties uit ── */
  useEffect(() => {
    if (!selected.has("tel-voip")) {
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete("tel-teams");
        telOptieIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, [selected]);

  /* ── Totaal berekening ── */
  const { total, breakdown, eenmalig } = useMemo(() => {
    const lines: { name: string; amount: number }[] = [];

    // Werkplek
    werkplekItems.forEach((item) => {
      if (!selected.has(item.id)) return;
      const qty = wpQty[item.id] || 1;
      lines.push({ name: `${item.name} (×${qty})`, amount: item.price * qty });
    });

    // Optioneel — gebruikers auto-berekend
    const sigQty = Math.max(totalWerkplekUsers, 10);
    optioneleItems.forEach((item) => {
      if (!selected.has(item.id)) return;
      if (item.id === "opt-signatures") {
        lines.push({ name: `${item.name} (×${sigQty})`, amount: item.price * sigQty });
      } else {
        lines.push({ name: `${item.name} (×${totalWerkplekUsers})`, amount: item.price * totalWerkplekUsers });
      }
    });

    // Internet
    internetItems.forEach((item) => {
      if (!selected.has(item.id)) return;
      lines.push({ name: item.name, amount: item.price });
    });

    // Netwerk
    netwerkItems.forEach((item) => {
      if (!selected.has(item.id)) return;
      const qty = unitQty[item.id] || 1;
      lines.push({ name: `${item.name} (×${qty})`, amount: item.price * qty });
    });

    // Telefonie
    if (centraleActive) {
      lines.push({ name: "VoIP Centrale", amount: CENTRALE_PRICE });

      // Nummers
      nummerOpties.forEach((n) => {
        if (!selectedNummers.has(n.id)) return;
        const qty = nummerQty[n.id] || 1;
        lines.push({ name: qty > 1 ? `${n.label} (×${qty})` : n.label, amount: n.price * qty });
      });

      // Telefonie gebruikers
      telefonieGebruikerItems.forEach((item) => {
        if (!selected.has(item.id)) return;
        const qty = telQty[item.id] || 1;
        lines.push({ name: `${item.name} (×${qty})`, amount: item.price * qty });
      });

      // Telefonie opties — per regel, max VoIP-aantal
      const voipQty = selected.has("tel-voip") ? (telQty["tel-voip"] || 1) : 0;
      telefonieOptieItems.forEach((item) => {
        if (!selected.has(item.id)) return;
        const qty = Math.min(telQty[item.id] || 1, voipQty);
        lines.push({ name: `${item.name} (×${qty})`, amount: item.price * qty });
      });
    }

    const total = lines.reduce((s, l) => s + l.amount, 0);

    // Eenmalige implementatiekosten
    let eenmalig = 0;
    werkplekItems.forEach((item) => {
      if (!selected.has(item.id) || !IMPLEMENTATIE_IDS.has(item.id)) return;
      const qty = wpQty[item.id] || 1;
      eenmalig += IMPLEMENTATIE_PRICE * qty;
    });

    // Eenmalige nummerkosten
    if (centraleActive) {
      nummerOpties.forEach((n) => {
        if (!selectedNummers.has(n.id) || !n.eenmalig) return;
        const qty = nummerQty[n.id] || 1;
        eenmalig += n.eenmalig * qty;
      });
    }

    return { total, breakdown: lines, eenmalig };
  }, [selected, wpQty, totalWerkplekUsers, centraleActive, selectedNummers, nummerQty, telQty, unitQty]);

  

  /* ── Render helpers ── */
  const renderItemRow = (item: ConfigItem, priceDisplay: number) => {
    const isSelected = selected.has(item.id);
    return (
      <button
        key={item.id}
        onClick={() => toggle(item.id)}
        className={`flex w-full items-center justify-between rounded-lg border p-4 text-left transition-all ${isSelected ? "border-primary bg-accent shadow-sm" : "border-border hover:border-primary/30"}`}
      >
        <div className="flex items-center gap-3">
          <Checkbox checked={isSelected} />
          <div>
            <p className="text-sm font-medium flex items-center gap-1.5">{item.name} <ItemTooltip tooltip={item.tooltip} href={item.href} /></p>
            <p className="text-xs text-muted-foreground">{item.unit}</p>
          </div>
        </div>
        <span className="text-sm font-bold">€{fmt(priceDisplay)}</span>
      </button>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div className="grid gap-8 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-8">
        {/* ── Werkplek ── */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-card">
          <SectionHeader title="Werkplek" />
          <div className="space-y-2">
            {werkplekItems.map((item) => {
              const isSelected = selected.has(item.id);
              const qty = wpQty[item.id] || 1;
              return (
                <div key={item.id} className={`rounded-lg border p-4 transition-all ${isSelected ? "border-primary bg-accent shadow-sm" : "border-border"}`}>
                  <button onClick={() => toggle(item.id)} className="flex w-full items-center justify-between text-left">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={isSelected} />
                      <div>
                        <p className="text-sm font-medium flex items-center gap-1.5">{item.name} <ItemTooltip tooltip={item.tooltip} href={item.href} /></p>
                        <p className="text-xs text-muted-foreground">{item.unit}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold">€{fmt(item.price * qty)}</span>
                  </button>
                  {isSelected && (
                    <div className="mt-3 ml-8 flex items-center gap-3">
                      <label className="text-xs text-muted-foreground">Aantal:</label>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setWpQty((p) => ({ ...p, [item.id]: Math.max(1, (p[item.id] || 1) - 1) }))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-sm font-bold hover:bg-muted">−</button>
                        <span className="min-w-[2rem] text-center text-sm font-bold">{qty}</span>
                        <button onClick={() => setWpQty((p) => ({ ...p, [item.id]: (p[item.id] || 1) + 1 }))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-sm font-bold hover:bg-muted">+</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Optionele werkplek diensten ── */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-card">
          <SectionHeader title="Optionele werkplek diensten" />
          {totalWerkplekUsers === 0 && (
            <p className="mb-3 text-xs text-muted-foreground">Selecteer eerst werkplekken hierboven om deze opties te activeren.</p>
          )}
          <div className="space-y-2">
            {optioneleItems.map((item) => {
              const isSelected = selected.has(item.id);
              const isSignatures = item.id === "opt-signatures";
              const autoQty = isSignatures ? Math.max(totalWerkplekUsers, 10) : totalWerkplekUsers;
              const disabled = totalWerkplekUsers === 0;
              return (
                <button
                  key={item.id}
                  onClick={() => !disabled && toggle(item.id)}
                  disabled={disabled}
                  className={`flex w-full items-center justify-between rounded-lg border p-4 text-left transition-all ${isSelected ? "border-primary bg-accent shadow-sm" : "border-border hover:border-primary/30"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox checked={isSelected} />
                    <div>
                      <p className="text-sm font-medium flex items-center gap-1.5">{item.name} <ItemTooltip tooltip={item.tooltip} href={item.href} /></p>
                      <p className="text-xs text-muted-foreground">
                        {isSignatures
                          ? `Automatisch ${autoQty} gebruikers (min. 10, op basis van werkplekken)`
                          : `Automatisch ${autoQty} gebruikers (op basis van werkplekken)`}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-bold">€{fmt(item.price * autoQty)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Internet ── */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-card">
          <SectionHeader title="Internet" />
          <div className="space-y-2">
            {internetItems.map((item) => renderItemRow(item, item.price))}
          </div>
        </div>

        {/* ── Netwerk ── */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-card">
          <SectionHeader title="Netwerk" />
          <p className="mb-3 text-xs text-muted-foreground">Router/firewall is altijd inbegrepen bij je internetabonnement.</p>
          <div className="space-y-2">
            {netwerkItems.map((item) => {
              const isSelected = selected.has(item.id);
              const qty = unitQty[item.id] || 1;
              return (
                <div key={item.id} className={`rounded-lg border p-4 transition-all ${isSelected ? "border-primary bg-accent shadow-sm" : "border-border"}`}>
                  <button onClick={() => toggle(item.id)} className="flex w-full items-center justify-between text-left">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={isSelected} />
                      <div>
                        <p className="text-sm font-medium flex items-center gap-1.5">{item.name} <ItemTooltip tooltip={item.tooltip} href={item.href} /></p>
                        <p className="text-xs text-muted-foreground">{item.unit}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold">€{fmt(item.price * qty)}</span>
                  </button>
                  {isSelected && (
                    <div className="mt-3 ml-8 flex items-center gap-3">
                      <label className="text-xs text-muted-foreground">Aantal:</label>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setUnitQty((p) => ({ ...p, [item.id]: Math.max(1, (p[item.id] || 1) - 1) }))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-sm font-bold hover:bg-muted">−</button>
                        <span className="min-w-[2rem] text-center text-sm font-bold">{qty}</span>
                        <button onClick={() => setUnitQty((p) => ({ ...p, [item.id]: (p[item.id] || 1) + 1 }))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-sm font-bold hover:bg-muted">+</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Telefonie ── */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-card">
          <SectionHeader title="Zakelijke Telefonie" />

          {/* Centrale toggle */}
          <button
            onClick={() => setCentraleActive(!centraleActive)}
            className={`flex w-full items-center justify-between rounded-lg border p-4 text-left transition-all ${centraleActive ? "border-primary bg-accent shadow-sm" : "border-border hover:border-primary/30"}`}
          >
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${centraleActive ? "bg-primary" : "bg-muted"}`}>
                {centraleActive ? <Phone className="h-5 w-5 text-primary-foreground" /> : <PhoneOff className="h-5 w-5 text-muted-foreground" />}
              </div>
              <div>
                <p className="text-sm font-medium">VoIP Centrale</p>
                <p className="text-xs text-muted-foreground">Verplicht als basis voor telefonie</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold">€{fmt(CENTRALE_PRICE)}/mnd</span>
              {centraleActive ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </div>
          </button>

          <AnimatePresence>
            {centraleActive && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                {/* Stap 1: Nummers */}
                <div className="mt-6 rounded-lg border border-border bg-muted/30 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">Stap 1</p>
                  <p className="text-sm font-semibold mb-1">Telefoonnummers</p>
                  <p className="text-xs text-muted-foreground mb-3">Onderstaande prijzen gelden voor nieuwe én het meenemen van bestaande nummers. Selecteer minimaal 1 nummer of nummerblok.</p>
                  <div className="space-y-2">
                    {nummerOpties.map((n) => {
                      const isSel = selectedNummers.has(n.id);
                      const qty = nummerQty[n.id] || 1;
                      return (
                        <div
                          key={n.id}
                          className={`rounded-lg border p-3 transition-all text-sm ${isSel ? "border-primary bg-accent shadow-sm" : "border-border"}`}
                        >
                          <button
                            onClick={() => toggleNummer(n.id)}
                            className="flex w-full items-center justify-between text-left"
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox checked={isSel} />
                              <span className="flex items-center gap-1.5">{n.label} <ItemTooltip tooltip={n.tooltip} href={n.href} /></span>
                            </div>
                            <div className="text-right">
                              <span className="font-bold">€{fmt(n.price * qty)} <span className="font-normal text-xs text-muted-foreground">{n.unit}</span></span>
                              {n.eenmalig ? (
                                <p className="text-xs text-muted-foreground">Eenmalig: €{fmt(n.eenmalig * qty)}</p>
                              ) : null}
                            </div>
                          </button>
                          {isSel && (
                            <div className="mt-3 ml-8 flex items-center gap-3">
                              <label className="text-xs text-muted-foreground">Aantal:</label>
                              <div className="flex items-center gap-1">
                                <button onClick={() => setNummerQty((p) => ({ ...p, [n.id]: Math.max(1, (p[n.id] || 1) - 1) }))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-sm font-bold hover:bg-muted">−</button>
                                <span className="min-w-[2rem] text-center text-sm font-bold">{qty}</span>
                                <button onClick={() => setNummerQty((p) => ({ ...p, [n.id]: (p[n.id] || 1) + 1 }))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-sm font-bold hover:bg-muted">+</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {!hasAtLeastOneNumber && (
                    <p className="mt-2 text-xs text-destructive">Selecteer minimaal 1 nummer om verder te gaan.</p>
                  )}
                </div>

                {/* Stap 2: Telefonie-gebruikers */}
                <div className={`mt-4 rounded-lg border border-border p-4 transition-opacity ${hasAtLeastOneNumber ? "bg-muted/30" : "bg-muted/10 opacity-50 pointer-events-none"}`}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">Stap 2</p>
                  <p className="text-sm font-semibold mb-1">Telefonie-gebruikers</p>
                  <p className="text-xs text-muted-foreground mb-3">Bellen met Teams is een aanvulling op VoIP en kan niet meer gebruikers hebben dan VoIP.</p>

                  <div className="space-y-2">
                    {telefonieGebruikerItems.map((item) => {
                      const isSelected = selected.has(item.id);
                      const isTeams = item.id === "tel-teams";
                      const voipQty = telQty["tel-voip"] || 1;
                      const maxQty = isTeams ? voipQty : 999;
                      const qty = Math.min(telQty[item.id] || 1, maxQty);
                      const teamsDisabled = isTeams && !selected.has("tel-voip");
                      return (
                        <div key={item.id} className={`rounded-lg border p-4 transition-all ${isSelected ? "border-primary bg-accent shadow-sm" : "border-border"} ${teamsDisabled ? "opacity-50" : ""}`}>
                          <button
                            onClick={() => !teamsDisabled && toggle(item.id)}
                            className="flex w-full items-center justify-between text-left"
                            disabled={teamsDisabled}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox checked={isSelected} />
                              <div>
                                <p className="text-sm font-medium flex items-center gap-1.5">{item.name} <ItemTooltip tooltip={item.tooltip} href={item.href} /></p>
                                <p className="text-xs text-muted-foreground">{item.unit}</p>
                                {teamsDisabled && <p className="text-xs text-muted-foreground italic">Selecteer eerst VoIP Aansluiting</p>}
                              </div>
                            </div>
                            <span className="text-sm font-bold">€{fmt(item.price * qty)}</span>
                          </button>
                          {isSelected && !teamsDisabled && (
                            <div className="mt-3 ml-8 flex items-center gap-3">
                              <label className="text-xs text-muted-foreground">Aantal:</label>
                              <div className="flex items-center gap-1">
                                <button onClick={() => setTelQty((p) => ({ ...p, [item.id]: Math.max(1, (p[item.id] || 1) - 1) }))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-sm font-bold hover:bg-muted">−</button>
                                <span className="min-w-[2rem] text-center text-sm font-bold">{qty}</span>
                                <button onClick={() => setTelQty((p) => ({ ...p, [item.id]: Math.min(maxQty, (p[item.id] || 1) + 1) }))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-sm font-bold hover:bg-muted">+</button>
                              </div>
                              {isTeams && <span className="text-xs text-muted-foreground">(max {voipQty})</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Stap 3: Opties */}
                <div className={`mt-4 rounded-lg border border-border p-4 transition-opacity ${hasAtLeastOneNumber ? "bg-muted/30" : "bg-muted/10 opacity-50 pointer-events-none"}`}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">Stap 3</p>
                  <p className="text-sm font-semibold mb-1">Eventuele opties</p>
                  <p className="text-xs text-muted-foreground mb-3">Opties zijn onafhankelijk van elkaar maar kunnen niet meer dan het aantal VoIP-aansluitingen zijn.</p>
                  <div className="mt-2 space-y-2">
                    {telefonieOptieItems.map((item) => {
                      const isSelected = selected.has(item.id);
                      const voipQty = selected.has("tel-voip") ? (telQty["tel-voip"] || 1) : 0;
                      const maxQty = voipQty;
                      const qty = Math.min(telQty[item.id] || 1, maxQty);
                      const disabled = !selected.has("tel-voip");
                      return (
                        <div key={item.id} className={`rounded-lg border p-4 transition-all ${isSelected ? "border-primary bg-accent shadow-sm" : "border-border"} ${disabled ? "opacity-50" : ""}`}>
                          <button
                            onClick={() => !disabled && toggle(item.id)}
                            className="flex w-full items-center justify-between text-left"
                            disabled={disabled}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox checked={isSelected} />
                              <div>
                                <p className="text-sm font-medium flex items-center gap-1.5">{item.name} <ItemTooltip tooltip={item.tooltip} href={item.href} /></p>
                                <p className="text-xs text-muted-foreground">{item.unit}</p>
                                {disabled && <p className="text-xs text-muted-foreground italic">Selecteer eerst VoIP Aansluiting</p>}
                              </div>
                            </div>
                            <span className="text-sm font-bold">€{fmt(item.price * qty)}</span>
                          </button>
                          {isSelected && !disabled && (
                            <div className="mt-3 ml-8 flex items-center gap-3">
                              <label className="text-xs text-muted-foreground">Aantal:</label>
                              <div className="flex items-center gap-1">
                                <button onClick={() => setTelQty((p) => ({ ...p, [item.id]: Math.max(1, (p[item.id] || 1) - 1) }))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-sm font-bold hover:bg-muted">−</button>
                                <span className="min-w-[2rem] text-center text-sm font-bold">{qty}</span>
                                <button onClick={() => setTelQty((p) => ({ ...p, [item.id]: Math.min(maxQty, (p[item.id] || 1) + 1) }))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-sm font-bold hover:bg-muted">+</button>
                              </div>
                              <span className="text-xs text-muted-foreground">(max {voipQty})</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </div>

      {/* ── Sticky totaal ── */}
      <div className="lg:col-span-1">
        <div className="sticky top-24 space-y-6">
          <div className="rounded-xl border border-border bg-card p-6 shadow-card">
            <p className="text-sm text-muted-foreground">Geschatte maandprijs</p>
            <p className="mt-2 text-4xl font-bold text-gradient">€{fmt(total)}</p>
            <p className="mt-1 text-xs text-muted-foreground">per maand, excl. BTW</p>

            {breakdown.length > 0 && (
              <ul className="mt-6 space-y-2">
                {breakdown.map((line, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{line.name}</span>
                    <span className="font-medium">€{fmt(line.amount)}</span>
                  </li>
                ))}
              </ul>
            )}

            {eenmalig > 0 && (
              <div className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Eenmalig</p>
                <div className="mt-1 flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">Implementatiekosten</span>
                  <span className="text-sm font-bold">€{fmt(eenmalig)}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">€{fmt(IMPLEMENTATIE_PRICE)} per werkplek · latere uitbreidingen kosteloos</p>
              </div>
            )}

            <div className="mt-6 border-t border-border pt-4">
              {!showForm ? (
                <Button variant="hero" className="w-full" onClick={() => setShowForm(true)} disabled={breakdown.length === 0}>
                  Offerte aanvragen
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleDownloadPDF();
                  }}
                >
                  <p className="text-xs text-muted-foreground">Vul je gegevens in om de offerte te downloaden als PDF.</p>
                  <input type="text" placeholder="Je naam *" required maxLength={100} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" value={formData.naam} onChange={(e) => setFormData({ ...formData, naam: e.target.value })} />
                  <input type="text" placeholder="Bedrijfsnaam" maxLength={100} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" value={formData.bedrijf} onChange={(e) => setFormData({ ...formData, bedrijf: e.target.value })} />
                  <input type="email" placeholder="E-mailadres *" required maxLength={255} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  <input type="tel" placeholder="Telefoonnummer" maxLength={20} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" value={formData.telefoon} onChange={(e) => setFormData({ ...formData, telefoon: e.target.value })} />
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" type="button" onClick={handlePreviewPDF} disabled={!formData.naam || !formData.email}>
                      <Eye className="mr-1 h-4 w-4" />
                      Preview
                    </Button>
                    <Button variant="hero" className="flex-1" type="submit">
                      <Download className="mr-1 h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>
                </motion.form>
              )}
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            Prijzen zijn indicatief en afkomstig van onze dienstpagina's. We maken graag een offerte op maat.
          </p>
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
};

export default PriceConfigurator;
